import React, {useEffect, useState, useContext} from "react";
import axios from "axios";
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTimes} from '@fortawesome/free-solid-svg-icons'
import {Circle, Map, Marker, Popup, TileLayer, ZoomControl, useLeaflet} from "react-leaflet";
import {DrawControl} from "./DrawControl";
import FilterContext from '../FilterContext';
import {DynamicGeoJson, DynamicGeoJsonShapes, JITTER_RADIUS_M, LOCATION_OBFUSCATION_ENABLED} from "./DynamicGeoJson";
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import './CrimeMap.css'
import {Sidebar} from "../sidebar/Sidebar";
import {Spinner} from "../Spinner";

import CustomModal from "./CrimeTip/Modal";
import "bootstrap/dist/css/bootstrap.css";
import packageJson from '../../package.json';



// this is required or else icons will not load
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.0.0/images/marker-icon.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.0.0/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.0.0/images/marker-shadow.png',
});

const SEARCH_MARKER_COLOR = '#d62b2b';

const searchIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const SearchLocationLayer = () => {
    const [context] = useContext(FilterContext);
    const l = useLeaflet();
    const loc = context.searchLocation;
    const region = context.filters.region;
    const radius = region && region['@type'] === 'circle' ? region.radius : null;

    useEffect(() => {
        if (loc && l && l.map) {
            l.map.setView([loc.lat, loc.lon], 15);
        }
    }, [loc && loc.lat, loc && loc.lon, l]);

    if (!loc || radius == null) return null;

    return (
        <>
            <Marker position={[loc.lat, loc.lon]} icon={searchIcon}>
                {loc.label && <Popup>{loc.label}</Popup>}
            </Marker>
            <Circle
                center={[loc.lat, loc.lon]}
                radius={radius + (LOCATION_OBFUSCATION_ENABLED ? JITTER_RADIUS_M : 0)}
                color={SEARCH_MARKER_COLOR}
                fillColor={SEARCH_MARKER_COLOR}
            />
        </>
    );
};

export const CrimeMap = () => {
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarOpen2, setSnackbarOpen2] = React.useState(false);

    //for message after tip is submitted successfully
    const [tipSuccessMessageOpen, setTipSuccessMessageOpen] = React.useState({
        open: false
    });
    const handleTipSuccessMessageOpen = () => {
        setTipSuccessMessageOpen({open: true})
    };
    const handleTipSuccessMessageClose = () => {
        setTipSuccessMessageOpen({open: false})
    };

    const [context] = useContext(FilterContext);

    const [noResultsSnackbarOpen, setNoResultsSnackbarOpen] = React.useState(false);

    const handleSnackbarClose = (event, reason) => {
        if (reason !== 'clickaway') {
            setSnackbarOpen(false);
        }
    };
    const handleSnackbarClose2 = (event, reason) => {
        if (reason !== 'clickaway') {
            setSnackbarOpen2(false);
        }
    };

    //used for crime tip form/modal
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleOpen = () => {
        resetForm();
        setShow(true);
    }
    //form content
    const [tip, setTip] = useState('');
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const resetForm = () => {
        setTip('');
        setName('');
        setPhone('');
        setEmail('');
    }

    const handleSubmission = (e) => {
        //handle form submission
        e.preventDefault();  //prevent page refresh
        const crimeTip = {
            agencyCode: context.agency,
            schemaId: context.schema,
            incidentId: document.getElementById("incId").textContent,
            crimeTip: tip,
            name: name,
            phone: phone,
            email: email
        };

        if(tip !== "") {
            //call endpoint here
            axios.post(packageJson.homepage+'/api/v1/crimeTip', crimeTip)
                .then(
                    ret => {
                        console.log(ret)    //TODO: remove when done
                        if(ret.status !== 200) {
                            alert("Tip failed to submit!");
                        } else {
                            //display success message
                            handleTipSuccessMessageOpen()
                        }
                    }
                )
                .catch(
                    error => {
                        alert(error);
                });

            handleClose();
        }
    }

    useEffect(() => {
        const totalFeatures = (context.results?.features?.length ?? 0) + (context.calls?.features?.length ?? 0);
        const noResults = context.results != null && context.calls != null && totalFeatures === 0;
        setNoResultsSnackbarOpen(noResults);
    }, [context.results, context.calls]);

    useEffect(() => {
        if (context.filters.showIncidents==='Y') {
            setSnackbarOpen(true);
        }
    }, [context.results]);

    useEffect(() => {
        if (context.filters.showCallsForService==='Y') {
            setSnackbarOpen2(true);
        }
    }, [context.calls]);

    if (context.error) {
        return <p>Error: {JSON.stringify(context.error.message)}</p>
    } else if (!context.results && !context.calls ) {
        return <Spinner />;
    } else {
        return (
            <>
                <Sidebar/>
                <Map zoom={10} zoomControl={false}>
                    <TileLayer
                        attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.osm.org/{z}/{x}/{y}.png"
                    />
                    <ZoomControl position="topright"/>
                    <DrawControl/>
                    <SearchLocationLayer/>
                    <DynamicGeoJson geoJson={context.results} flag={"results"} onOpen={handleOpen}/>
                    <DynamicGeoJson geoJson={context.calls} flag={"calls"} onOpen={handleOpen}/>
                    <DynamicGeoJsonShapes
                        geoJson={context.shapes?.geojson?.features?.find(it => it.id === context.filters.shapeId?.toString())}/>
                    <Snackbar
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        open={snackbarOpen}
                        autoHideDuration={5000}
                        onClose={handleSnackbarClose}
                        message={context.results ? context.results.countMessage : ""}
                        action={
                            <React.Fragment>
                                <IconButton size="small" aria-label="close" color="inherit"
                                            onClick={handleSnackbarClose}>
                                    <FontAwesomeIcon icon={faTimes}/>
                                </IconButton>
                            </React.Fragment>
                        }
                    />
                    <Snackbar
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        open={snackbarOpen2}
                        autoHideDuration={5000}
                        onClose={handleSnackbarClose2}
                        message= {context.calls ? context.calls.countMessage : ""}
                        action={
                            <React.Fragment>
                                <IconButton size="small" aria-label="close" color="inherit"
                                            onClick={handleSnackbarClose2}>
                                    <FontAwesomeIcon icon={faTimes}/>
                                </IconButton>
                            </React.Fragment>
                        }
                    />
                    <Snackbar
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        open={tipSuccessMessageOpen.open}
                        onClose={()=>{handleTipSuccessMessageClose()}}
                        autoHideDuration={3000}
                        message="Crime tip Submitted"
                    />
                    <Snackbar
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        open={noResultsSnackbarOpen}
                        onClose={(event, reason) => {
                            if (reason !== 'clickaway') setNoResultsSnackbarOpen(false);
                        }}
                        message="No incidents or calls found. Try expanding the date range in the When filter."
                        action={
                            <IconButton size="small" aria-label="close" color="inherit"
                                        onClick={() => setNoResultsSnackbarOpen(false)}>
                                <FontAwesomeIcon icon={faTimes}/>
                            </IconButton>
                        }
                    />
                </Map>
                {LOCATION_OBFUSCATION_ENABLED && (
                    <div className="location-disclaimer">
                        <span style={{color: '#e65c00', fontSize: '15px'}}>&#9888;</span> All locations are approximate. Exact addresses have been obfuscated to protect privacy.
                    </div>
                )}
                {/*crime tip submission form*/}
                <CustomModal
                    show={show}
                    tip={tip}
                    setTip={setTip}
                    name={name}
                    setName={setName}
                    phone={phone}
                    setPhone={setPhone}
                    email={email}
                    setEmail={setEmail}
                    onClose={handleClose}
                    onSubmit={handleSubmission}
                />
            </>
        );
    }
}
